/**
 * Drag-and-drop photo attacher — works for ALL users (no Pro gate).
 *
 * Why this exists separately from PhotoEstimator:
 *   PhotoEstimator runs photos through GPT-5.2 vision and is Pro-only.
 *   This component just attaches photos to the PDF — no AI, no cost,
 *   free for everyone. Drives word-of-mouth without cannibalizing Pro
 *   conversions (Pro still differentiates on AI scope generation).
 *
 * Photos are stored as data URLs in component state so they survive
 * tab refresh poorly (intentional — quotes are session-bound), and
 * pushed up to `setProjectPhotos` so the PDF generator picks them up.
 *
 * AUTO-COMPRESSION:
 *   Modern phones produce 5-12 MB JPEGs that blow past upload limits and
 *   bloat the final PDF. We resize to max 1600px on the longest edge and
 *   target ~1 MB per file using `browser-image-compression`. Compression
 *   runs in a Web Worker so the UI stays responsive. We accept raw files
 *   up to 25 MB because the compression step is what we trust, not the
 *   original size.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { Camera, Upload, X, ImagePlus, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const MAX_PHOTOS = 6;
const MAX_RAW_BYTES = 25 * 1024 * 1024; // 25 MB hard ceiling on input
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

// Compression target — tuned for PDF embedding at A4 width.
// 1600px on the longest edge prints crisp on Letter/A4 at 200 DPI and
// keeps each photo well under 1 MB after JPEG re-encode.
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.82,
};

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const QuotePhotoAttacher = ({ projectPhotos = [], setProjectPhotos }) => {
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const { toast } = useToast();

  // Auto-clear errors after a few seconds so they don't pile up.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 4500);
    return () => clearTimeout(t);
  }, [error]);

  const addFiles = useCallback(
    async (files) => {
      setError('');
      const room = MAX_PHOTOS - projectPhotos.length;
      if (room <= 0) {
        setError(`You've hit the max of ${MAX_PHOTOS} photos. Remove one to add another.`);
        return;
      }
      const list = Array.from(files).slice(0, room);
      const accepted = [];
      let totalBefore = 0;
      let totalAfter = 0;

      setBusy(true);
      for (const f of list) {
        if (!ACCEPTED.includes(f.type)) {
          setError(`"${f.name}" — only JPG, PNG, and WEBP are supported.`);
          continue;
        }
        if (f.size > MAX_RAW_BYTES) {
          setError(`"${f.name}" is over 25 MB. Please pick a smaller file.`);
          continue;
        }
        try {
          // Always run through compression — even small files benefit from
          // the resize down to 1600px (PDF doesn't need 4000px source).
          const compressed = await imageCompression(f, COMPRESSION_OPTIONS);
          totalBefore += f.size;
          totalAfter += compressed.size;
          accepted.push(await readAsDataURL(compressed));
        } catch (err) {
          // Fallback: if the worker chokes on an edge-case PNG, embed the
          // original so the user isn't blocked. PDF will be larger but works.
          // eslint-disable-next-line no-console
          console.warn('Photo compression fallback for', f.name, err);
          try {
            totalBefore += f.size;
            totalAfter += f.size;
            accepted.push(await readAsDataURL(f));
          } catch {
            setError(`Couldn't read "${f.name}". Try a different file.`);
          }
        }
      }
      setBusy(false);

      if (accepted.length) {
        setProjectPhotos((prev) => [...prev, ...accepted]);
        // Only celebrate the savings when it's actually meaningful.
        const saved = totalBefore - totalAfter;
        if (saved > 100 * 1024) {
          toast({
            title: 'Photos optimized',
            description: `${formatBytes(totalBefore)} → ${formatBytes(totalAfter)} (saved ${formatBytes(saved)}). PDFs will send faster.`,
          });
        }
      }
    },
    [projectPhotos.length, setProjectPhotos, toast]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const onSelect = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = ''; // allow re-selecting the same file
  };
  const removeAt = (idx) => {
    setProjectPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid="quote-photo-attacher">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          <ImagePlus className="w-4 h-4 text-blue-600" />
          Attach Photos to Quote (Optional)
        </label>
        <span className="text-xs text-slate-400">{projectPhotos.length}/{MAX_PHOTOS}</span>
      </div>

      <div
        role="region"
        aria-label="Drag and drop photos to attach"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={[
          'rounded-xl border-2 border-dashed p-4 text-center transition-colors',
          busy ? 'cursor-wait opacity-80' : 'cursor-pointer',
          drag
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50/50 hover:bg-slate-100/60',
        ].join(' ')}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          multiple
          onChange={onSelect}
          disabled={busy}
          className="hidden"
          data-testid="photo-file-input"
        />
        <div className="flex items-center justify-center gap-2 text-slate-600">
          {busy ? (
            <>
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <span className="text-sm">
                <strong className="text-blue-700">Optimizing photos…</strong>
              </span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-blue-600" />
              <span className="text-sm">
                <strong className="text-blue-700">Drag photos here</strong> or click to browse
              </span>
            </>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          JPG / PNG / WEBP • auto-resized to 1600px • appears in your PDF gallery
        </p>
      </div>

      {error && (
        <p
          className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
          data-testid="photo-attacher-error"
        >
          {error}
        </p>
      )}

      {projectPhotos.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="photo-thumbnail-grid">
          {projectPhotos.map((src, i) => (
            <div
              key={i}
              className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-video"
              data-testid={`photo-thumb-${i}`}
            >
              <img
                src={typeof src === 'string' ? src : (src?.dataUrl || src?.url || '')}
                alt={`Quote photo ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                aria-label={`Remove photo ${i + 1}`}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-red-600 text-white opacity-80 group-hover:opacity-100 transition-opacity"
                data-testid={`photo-thumb-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent text-white text-[10px] px-1.5 py-0.5">
                <Camera className="inline w-3 h-3 mr-1" />
                Photo {i + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuotePhotoAttacher;
